import { useEffect, useMemo } from 'react';
import { minTwoDigits } from '@acorn-portal/shared/src/utils';
import { IOptions } from './date-picker.types';

/**
 * Props for the useDateTimeSelection hook
 */
interface UseDateTimeSelectionProps {
	value?: Date;
	hour?: string;
	minute?: string;
	setHour?: (values: string) => void;
	setMinute?: (values: string) => void;
	hideDates?: number[];
}

/**
 * Time information computed from current time with rounding rules
 */
interface TimeInfo {
	currentHour: number;
	currentMinute: number;
	roundedHour: number;
	roundedMinute: number;
	nextIncrementHour: number;
	nextIncrementMinute: number;
	shouldAdvanceHour: boolean;
	isEarlyInHour: boolean;
	isExactInterval: boolean;
}

/**
 * Hook for handling date/time selection with special rounding rules
 * - Minutes are rounded to 15-minute intervals
 * - When on exact intervals, the next increment is used for selection
 */
export const useDateTimeSelection = ({
	value,
	hour,
	minute,
	setHour,
	setMinute,
	hideDates,
}: UseDateTimeSelectionProps) => {
	// Check if selected date is today
	const isToday = useMemo(() => {
		if (!value) return false;

		const today = new Date();
		return (
			value.getDate() === today.getDate() &&
			value.getMonth() === today.getMonth() &&
			value.getFullYear() === today.getFullYear()
		);
	}, [value]);

	// Calculate time information with rounding rules
	const timeInfo = useMemo((): TimeInfo => {
		const now = new Date();
		const nowHours = now.getHours();
		const nowMinutes = now.getMinutes();

		// Time values after computation
		const { roundedMinute, roundedHour, nextIncrementMinute, nextIncrementHour } = computeTimeValues(
			nowHours,
			nowMinutes,
		);

		// Check if we're exactly on a 15-minute interval
		const isExactInterval = nowMinutes % 15 === 0;

		// Determine if we're advancing to the next hour based on rounded minutes
		const shouldAdvanceHour = roundedMinute === 45;

		// Calculate current hour for filtering available hours
		const calculatedHour = shouldAdvanceHour ? (roundedHour + 1) % 24 : roundedHour;

		return {
			currentHour: calculatedHour,
			currentMinute: roundedMinute,
			roundedHour,
			roundedMinute,
			nextIncrementHour,
			nextIncrementMinute,
			shouldAdvanceHour,
			isEarlyInHour: roundedMinute >= 0 && roundedMinute < 15,
			isExactInterval,
		};
	}, []);

	const getHours = (): Array<IOptions> => {
		const hours: Array<IOptions> = [];
		const maxHours = 23;
		for (let i = 0; i <= maxHours; i += 1) {
			hours.push({
				label: minTwoDigits(i),
				value: minTwoDigits(i),
			});
		}

		return hours;
	};

	// Hours dropdown options
	const hoursOptions = useMemo(() => {
		if (!isToday) return getHours();

		// Determine which hour to use for filtering
		const filterHour = timeInfo.isExactInterval ? timeInfo.nextIncrementHour : timeInfo.roundedHour;

		return getHours().filter((option) => parseInt(option.value as string) >= filterHour);
	}, [isToday, timeInfo]);

	const getMinutes = (maxMinutes: number = 60, interval: number = 15): Array<IOptions> => {
		const minutes: Array<IOptions> = [];

		for (let i = 0; i < maxMinutes; i += interval) {
			const string = minTwoDigits(i);

			minutes.push({
				label: string,
				value: string,
			});
		}

		return minutes;
	};

	// Minutes dropdown options
	const minutesOptions = useMemo(() => {
		const allMinutes = getMinutes();

		if (!isToday || !hour) return allMinutes;

		const selectedHour = parseInt(hour);

		if (timeInfo.isExactInterval && selectedHour === timeInfo.nextIncrementHour) {
			return allMinutes.filter((option) => parseInt(option.value as string) >= timeInfo.nextIncrementMinute);
		} else if (selectedHour === timeInfo.roundedHour) {
			// Regular case - show minutes from rounded minute forward
			return allMinutes.filter((option) => parseInt(option.value as string) >= timeInfo.roundedMinute);
		}

		return allMinutes;
	}, [isToday, hour, timeInfo]);

	// Update hour when needed
	useEffect(() => {
		if (!isToday || !setHour) return;

		if (!hour) return;
		const hourValue = parseInt(hour);

		if (timeInfo.isExactInterval) {
			if (hourValue < timeInfo.nextIncrementHour) {
				setHour(timeInfo.nextIncrementHour.toString().padStart(2, '0'));
			}
		} else if (hourValue < timeInfo.roundedHour) {
			setHour(timeInfo.roundedHour.toString().padStart(2, '0'));
		}
	}, [isToday, hour, timeInfo, setHour]);

	// Update minute when needed
	useEffect(() => {
		if (!isToday || !hour || !minute || !setMinute) return;

		const selectedHour = parseInt(hour);
		const selectedMinute = parseInt(minute);

		// Handle special cases in priority order

		// Case 1: Exact intervals - move to next increment
		if (handleExactIntervalCase(timeInfo, selectedHour, selectedMinute, setHour, setMinute)) {
			return;
		}

		// Case 2: Current hour with minutes less than rounded minute
		if (selectedHour === timeInfo.roundedHour && selectedMinute < timeInfo.roundedMinute) {
			setMinute(timeInfo.roundedMinute.toString().padStart(2, '0'));
			return;
		}

		// Case 3: Hour is less than rounded hour
		if (selectedHour < timeInfo.roundedHour) {
			if (setHour) {
				setHour(timeInfo.roundedHour.toString().padStart(2, '0'));
			}
			setMinute(timeInfo.roundedMinute.toString().padStart(2, '0'));
		}
	}, [isToday, hour, minute, setMinute, setHour, timeInfo]);

	// Handle hour change
	const handleHourChange = (newHour: string) => {
		if (!setHour) return;

		setHour(newHour);

		if (!isToday || !minute || !setMinute) return;

		const hourValue = parseInt(newHour);
		const minuteValue = parseInt(minute);

		// Update minute when hour changes
		if (timeInfo.isExactInterval && hourValue === timeInfo.roundedHour && minuteValue <= timeInfo.roundedMinute) {
			setMinute(timeInfo.nextIncrementMinute.toString().padStart(2, '0'));
		} else if (hourValue === timeInfo.roundedHour && minuteValue < timeInfo.roundedMinute) {
			setMinute(timeInfo.roundedMinute.toString().padStart(2, '0'));
		}
	};

	// Determine if a date should be disabled
	const disableDates = (date: Date | undefined) => {
		if (!hideDates || !date || hideDates.length === 0) return false;
		return hideDates.includes(date.getDate());
	};

	return {
		isToday,
		timeInfo,
		hoursOptions,
		minutesOptions,
		disableDates,
		handleHourChange,
	};
};

// Helper functions

/**
 * Compute rounded time values and next increments based on current time
 */
function computeTimeValues(nowHours: number, nowMinutes: number) {
	// Round to nearest 15 minute interval with special handling for 30
	const minuteRemainder = nowMinutes % 15;
	let roundedMinute = 0;

	roundedMinute = nowMinutes + (15 - minuteRemainder);

	// Handle minute overflow (60) and hour increment
	let roundedHour = nowHours;
	if (roundedMinute === 60) {
		roundedMinute = 0;
		roundedHour = (nowHours + 1) % 24;
	}

	// Calculate next increment after the rounded time
	let nextIncrementMinute = roundedMinute + 15;
	let nextIncrementHour = roundedHour;

	if (nextIncrementMinute === 60) {
		nextIncrementMinute = 0;
		nextIncrementHour = (roundedHour + 1) % 24;
	}

	return {
		roundedMinute,
		roundedHour,
		nextIncrementMinute,
		nextIncrementHour,
	};
}

/**
 * Handle the case when time is on an exact interval (0, 15, 45)
 * Returns true if the case was handled
 */
function handleExactIntervalCase(
	timeInfo: TimeInfo,
	selectedHour: number,
	selectedMinute: number,
	setHour?: (values: string) => void,
	setMinute?: (values: string) => void,
): boolean {
	if (!timeInfo.isExactInterval || !setMinute) return false;

	if (selectedHour === timeInfo.roundedHour && selectedMinute === timeInfo.roundedMinute) {
		// Move to the next increment
		if (timeInfo.nextIncrementHour !== selectedHour && setHour) {
			setHour(timeInfo.nextIncrementHour.toString().padStart(2, '0'));
		}
		setMinute(timeInfo.nextIncrementMinute.toString().padStart(2, '0'));
		return true;
	}

	return false;
}
